import type { Prisma } from '@prisma/client';

/**
 * Filtros de listagem de imóveis aplicáveis via query params nas landing pages
 * de Google Ads (`/imoveis/[finalidade]/[tipo]/[bairro]?...`).
 *
 * Convenções importantes (ver Passo 0 / CLAUDE.md):
 *  - `referencia` é EXCLUSIVAMENTE metadata/SEO. Nunca entra no WHERE do Prisma
 *    nem na chave de cache (mesma query → mesmos imóveis, só a metadata muda).
 *  - `status` (status de obra/entrega, ex.: "pronto-para-morar") NÃO possui coluna
 *    correspondente em `next_imoveis` — a coluna `status` é o status do anúncio
 *    (active/sold) e já é fixada em 'active'. Portanto `status` também é tratado
 *    como SOMENTE metadata (não entra no WHERE nem na chave de cache).
 */
export interface ImoveisFilters {
  quartos?: number;
  suites?: number;
  vagas?: number;
  area_min?: number;
  area_max?: number;
  preco_min?: number;
  preco_max?: number;
  status?: string; // ex: 'pronto-para-morar' — SOMENTE metadata (sem coluna no schema)
  categoria?: string; // ex: 'alto-padrao' → is_luxury (Boolean)
  tipo?: string; // ex: 'cobertura' — subtipo; mapeia para property_type
  referencia?: string; // ex: 'parque-areiao' — SOMENTE metadata, nunca Prisma
}

export function parseFilters(
  searchParams: Record<string, string | string[] | undefined>
): ImoveisFilters {
  const num = (v: string | string[] | undefined): number | undefined => {
    const n = Number(Array.isArray(v) ? v[0] : v);
    return Number.isFinite(n) && n > 0 ? n : undefined;
  };
  const str = (v: string | string[] | undefined): string | undefined => {
    const s = Array.isArray(v) ? v[0] : v;
    return s?.trim().toLowerCase() || undefined;
  };
  return {
    quartos: num(searchParams.quartos),
    suites: num(searchParams.suites),
    vagas: num(searchParams.vagas),
    area_min: num(searchParams.area_min),
    area_max: num(searchParams.area_max),
    preco_min: num(searchParams.preco_min),
    preco_max: num(searchParams.preco_max),
    status: str(searchParams.status),
    categoria: str(searchParams.categoria),
    tipo: str(searchParams.tipo),
    referencia: str(searchParams.referencia),
  };
}

// ── Mapas de slug → label de exibição (metadata/SEO) ────────────────────────
export const STATUS_LABELS: Record<string, string> = {
  'pronto-para-morar': 'Prontos para Morar',
};

export const CATEGORIA_LABELS: Record<string, string> = {
  'alto-padrao': 'Alto Padrão',
};

export const TIPO_LABELS: Record<string, string> = {
  cobertura: 'Coberturas',
  flat: 'Flats',
  duplex: 'Duplex',
};

// Label de exibição para o h1 da listagem quando ?tipo= (subtipo) está presente.
// Alinha o h1 visível ao title/metadata para Quality Score do Google Ads.
export const TIPO_H1_LABELS: Record<string, string> = {
  cobertura: 'Coberturas',
  flat: 'Flats',
  studio: 'Studios',
};

export const REFERENCIA_LABELS: Record<string, string> = {
  'parque-areiao': 'Parque Areião',
  flamboyant: 'Shopping Flamboyant',
  'lago-das-rosas': 'Lago das Rosas',
};

// ── Mapas de slug → valor real no banco ─────────────────────────────────────
// Subtipo: 'cobertura' e 'flat' são valores reais de `property_type` (ver
// src/lib/property-types.ts). 'duplex' não existe no banco → só metadata.
export const TIPO_DB_VALUES: Record<string, string> = {
  cobertura: 'cobertura',
  flat: 'flat',
};

// Categoria: não é string no schema, é a flag booleana `is_luxury`.
export const CATEGORIA_DB_FILTER: Record<string, Prisma.next_imoveisWhereInput> = {
  'alto-padrao': { is_luxury: true },
};

/**
 * Monta as cláusulas WHERE do Prisma a partir dos filtros.
 *
 * Retorna apenas as cláusulas dos filtros — deve ser combinada (spread) com o
 * WHERE base já existente na page (tenant_id, transaction_type, status:'active'…).
 * Por vir DEPOIS do base, `tipo` (subtipo) sobrescreve intencionalmente o
 * `property_type` derivado da rota.
 *
 * NUNCA inclui `referencia` (metadata) nem `status` (sem coluna no schema).
 */
export function buildImoveisFilterWhere(
  filters: ImoveisFilters
): Prisma.next_imoveisWhereInput {
  const where: Prisma.next_imoveisWhereInput = {};

  if (filters.quartos) where.bedrooms = { gte: filters.quartos };
  if (filters.suites) where.suites = { gte: filters.suites };
  if (filters.vagas) where.parking_spaces = { gte: filters.vagas };

  if (filters.area_min || filters.area_max) {
    where.built_area = {
      ...(filters.area_min ? { gte: filters.area_min } : {}),
      ...(filters.area_max ? { lte: filters.area_max } : {}),
    };
  }

  if (filters.preco_min || filters.preco_max) {
    where.price = {
      ...(filters.preco_min ? { gte: filters.preco_min } : {}),
      ...(filters.preco_max ? { lte: filters.preco_max } : {}),
    };
  }

  if (filters.categoria && CATEGORIA_DB_FILTER[filters.categoria]) {
    Object.assign(where, CATEGORIA_DB_FILTER[filters.categoria]);
  }

  if (filters.tipo) {
    const dbType = TIPO_DB_VALUES[filters.tipo];
    if (dbType) where.property_type = dbType;
  }

  // status (obra/entrega): sem coluna correspondente → não filtra (só metadata).
  // referencia: nunca entra no WHERE.

  return where;
}

/**
 * Fragmento determinístico da chave de cache derivado dos filtros que
 * efetivamente alteram o resultado da query.
 *
 * Exclui `referencia` E `status` (ambos só afetam metadata, não a query),
 * garantindo que `/setor-marista` e `/setor-marista?referencia=parque-areiao`
 * compartilhem o mesmo cache.
 */
export function buildFiltersCacheKey(filters: ImoveisFilters): string {
  const { referencia: _ref, status: _status, ...rest } = filters;
  const parts = Object.entries(rest)
    .filter(([, v]) => v !== undefined)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, v]) => `${k}:${v}`);
  return parts.join('|') || 'sem-filtro';
}
